import { useCallback } from 'react';

import { KlokhuisError } from '@klokhuis/core/ApiClient';
import {
  FindListingsResponse,
  ListingRequest,
  OpResponseCode,
} from '@klokhuis/core/ApiClient/Klokhuis';
import { AppServicesContext } from '@klokhuis/core/AppServices';
import { reportError } from '@klokhuis/core/Errors';
import { ListingsContext } from '@klokhuis/modules/Properties/context/ListingsContext';
import { ListingActionTypes } from '@klokhuis/modules/Properties/context/types';

export const useListingStateDispatcher = () => {
  const { dispatch } = ListingsContext.useContext();
  const { apiClient } = AppServicesContext.useContext();

  const listingStateDispatcher = useCallback(
    async (_values: Partial<ListingRequest>) => {
      dispatch({
        type: ListingActionTypes.UPDATE_LISTING_LOADING_STATE,
        payload: true,
      });

      const page = _values.pageNo || 1;
      const listingTypeIds = _values.listingTypeIds
        ?.map(id => String(id))
        .join(',');

      try {
        let response: FindListingsResponse;

        // Check if it's a seller page, city page, or all listings
        if (_values.sellerSlug) {
          // For seller page, we would need sellerId - this is typically handled in getServerSideProps
          // For now, fallback to city-based search
          response = await apiClient.listing.findListingsByCity({
            cityId: _values.location?.id?.toString() || '0',
            page,
            listingTypeIds,
          });
        } else if (_values.location?.id) {
          // If city is selected, fetch listings for that city
          response = await apiClient.listing.findListingsByCity({
            cityId: _values.location?.id?.toString() || '0',
            page,
            listingTypeIds,
          });
        } else {
          // If no city is selected, fetch all available listings
          response = await apiClient.listing.findAllListings({
            page,
            listingTypeIds,
          });
        }

        if (response) {
          dispatch({
            type: ListingActionTypes.UPDATE_LISTING,
            payload: response,
          });
          dispatch({
            type: ListingActionTypes.UPDATE_LISTING_LOADING_STATE,
            payload: false,
          });
        }
      } catch (error: unknown | KlokhuisError) {
        reportError(error as Error, {
          extra: {
            context: 'Listing context state update failed',
            metaData: { error },
          },
          tags: {},
        });
        dispatch({
          type: ListingActionTypes.UPDATE_LISTING_LOADING_STATE,
          payload: false,
        });
      }
    },
    [apiClient.listing, dispatch],
  );

  return { listingStateDispatcher };
};
